import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/ru";
dayjs.locale("ru");
dayjs.extend(timezone);
dayjs.extend(utc);
const dateFormat = "DD.MM.YYYY";
const dateTimeFormat = "DD.MM.YYYY HH:mm";
const timeFormat = "HH:mm";
const dxDateTimeFormat = "DD.MM.YYYY, HH:mm";
const purchaseDisplayDateTimeFormat = "DD.MM.YYYY HH:mm:ss";
const fileTimeStamp = "YYYY.MM.DD HH:mm:ss";
const apiDateTimeFormatSpecial = "YYYY-MM-DD HH:mm:ss";
const apiDateFormat = "YYYY-MM-DD";
const apiDateTimeFormat = "YYYY-MM-DDTHH:mm:ss";
const prizeApiStartDateFormat = "YYYY-MM-DD 00:00:00";
const prizeApiEndDateFormat = "YYYY-MM-DD 23:59:59";
const apiDateFormatEndDay = "YYYY-MM-DD 23:59:59";
const dateInvalid = "Неверный формат даты";
export interface DateTimeMixins {
  dateFormat: string;
  dateTimeFormat: string;
  timeFormat: string;
  dxDateTimeFormat: string;
  apiDateFormat: string;
  apiDateTimeFormat: string;
  fileTimeStamp: string;
  prizeApiStartDateFormat: string;
  prizeApiEndDateFormat: string;
  apiDateFormatEndDay: string;
  purchaseDisplayDateTimeFormat: string;
  apiDateTimeFormatSpecial: string;
  getDate(date?: Date | string, sourceFormat?: string): string | undefined;
  getDateTime(date?: Date | string, sourceFormat?: string): string;
  getDateTomorrow(format?: string): string;
  formatDate(
    date?: Date | string,
    format?: string,
    sourceFormat?: string,
    timeZone?: number,
    trimLastMin?: boolean
  ): string;
  formatTimestamp(date: Date | string, sourceFormat: string): string | undefined;
  addMinutes(date: Date, minutes: number): Date;
  addHours(date: Date, hours: number): Date;
  addMonths(date: Date, hours: number): Date;
  addDays(date: Date, days: number): Date;
  addYears(date: Date, years: number): Date;
  formDate(date: string | undefined): Date | undefined;
  sortDateCell(date: Date | string | undefined): number;
  isDateInRange(dateFrom: Date, dateTo: Date, date?: Date): boolean;
  isRangesCross(range1: [Date, Date], range2: [Date, Date]): boolean;
  isAfter(beforeDate: Date, date?: Date): boolean;
  isBefore(beforeDate: Date, date?: Date): boolean;
}

export const dateTimeMixins: DateTimeMixins = {
  dateFormat: dateFormat,
  dateTimeFormat: dateTimeFormat,
  timeFormat: timeFormat,
  dxDateTimeFormat: dxDateTimeFormat,
  apiDateFormat: apiDateFormat,
  apiDateTimeFormat: apiDateTimeFormat,
  prizeApiStartDateFormat: prizeApiStartDateFormat,
  prizeApiEndDateFormat: prizeApiEndDateFormat,
  apiDateFormatEndDay: apiDateFormatEndDay,
  purchaseDisplayDateTimeFormat: purchaseDisplayDateTimeFormat,
  apiDateTimeFormatSpecial: apiDateTimeFormatSpecial,
  fileTimeStamp: fileTimeStamp,

  getDate(date?: Date | string, sourceFormat?: string) {
    //если дата шифрованнаяя, то оставляем как есть
    if (!date) {
      return undefined;
    }
    if (typeof date === "string" && date.includes("*")) {
      return date;
    }
    const asMoment = (date && dayjs(date, sourceFormat)) || dayjs(new Date());
    return (asMoment.isValid() && asMoment.format(dateFormat)) || dateInvalid;
  },
  getDateTime(date?: Date | string, sourceFormat?: string) {
    //если дата шифрованнаяя, то оставляем как есть
    if (typeof date === "string" && date.includes("*")) {
      return date;
    }
    const asMoment = (date && dayjs(date, sourceFormat)) || dayjs(new Date());
    return (asMoment.isValid() && asMoment.format(dateTimeFormat)) || dateInvalid;
  },
  getDateTomorrow(format?: string) {
    const asMoment = dayjs().add(1, "day");
    return asMoment.format(format ?? dateFormat);
  },
  formatDate(
    date?: Date | string | undefined,
    format?: string,
    sourceFormat?: string,
    timeZone?: number,
    trimLastMin = false
  ): string {
    //если дата шифрованнаяя, то оставляем как есть

    if (typeof date === "string" && date.includes("*")) {
      return date;
    }
    let asMoment = date ? dayjs(date, sourceFormat) : dayjs();

    if (!asMoment.isValid()) {
      return dateInvalid;
    }
    if (timeZone !== undefined) {
      asMoment = asMoment.utcOffset(timeZone * 60);
    }
    if (trimLastMin && asMoment.hour() === 0 && asMoment.minute() === 0 && asMoment.second() === 0) {
      asMoment = asMoment.subtract(1, "millisecond");
    }
    return asMoment.format(format);
  },
  formatTimestamp(date: Date | string, sourceFormat?: string) {
    const asMoment = dayjs(date, sourceFormat);
    return (asMoment.isValid() && asMoment.toDate().getTime().toString()) || undefined;
  },
  addMinutes(date: Date, minutes: number) {
    const newDate = new Date(date);
    newDate.setMinutes(newDate.getMinutes() + minutes);
    return newDate;
  },
  addHours(date: Date, hours: number) {
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + hours);
    return newDate;
  },
  addDays(date: Date, days: number) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  },
  addMonths(date: Date, months: number) {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  },
  addYears(date: Date, years: number) {
    const newDate = new Date(date);
    newDate.setUTCFullYear(newDate.getUTCFullYear() + years);
    return newDate;
  },
  formDate(date: string | undefined): Date | undefined {
    return date ? dayjs(date).toDate() : undefined;
  },
  sortDateCell(date: Date | string | undefined): number {
    return date ? (this.formDate(date.toString()?.toString())?.getTime() ?? 0) * -1 : 0;
  },
  isDateInRange(dateFrom: Date, dateTo: Date, date?: Date): boolean {
    const now = dayjs(date).utc();
    const from = dayjs(dateFrom).utc();
    const to = dayjs(dateTo).utc();
    return now >= from && now <= to;
  },
  isRangesCross(range1: [Date, Date], range2: [Date, Date]): boolean {
    const [start1, end1] = range1;
    const [start2, end2] = range2;
    return start1 <= end2 && start2 <= end1;
  },
  isAfter(afterDate: Date, date?: Date): boolean {
    const now = dayjs(date).utc();
    const to = dayjs(afterDate).utc();
    return now > to;
  },
  isBefore(beforeDate: Date, date?: Date): boolean {
    const now = dayjs(date).utc();
    const to = dayjs(beforeDate).utc();
    return now < to;
  },
};

export default {
  methods: dateTimeMixins,
};
